export const getAnchoredRemainingSeconds = (
  serverTimeLeft: number,
  syncedAtMs: number,
  nowMs: number = Date.now()
): number => {
  const elapsedSeconds = Math.floor((nowMs - syncedAtMs) / 1000);
  return Math.max(0, serverTimeLeft - elapsedSeconds);
};

interface NormalizeAssessmentTimeLeftOptions {
  allowTimeLimitFallback?: boolean;
}

export const normalizeAssessmentTimeLeft = (
  timeLeft: number | null | undefined,
  timeLimit: number | null | undefined,
  { allowTimeLimitFallback = false }: NormalizeAssessmentTimeLeftOptions = {}
): number | null => {
  if (typeof timeLeft === "number" && Number.isFinite(timeLeft)) {
    return Math.max(0, timeLeft);
  }

  if (
    allowTimeLimitFallback &&
    typeof timeLimit === "number" &&
    Number.isFinite(timeLimit)
  ) {
    return Math.max(0, timeLimit);
  }

  return null;
};

const SECONDS_PER_DAY = 86_400;
const SECONDS_PER_HOUR = 3_600;

export const getAssessmentCountdownDays = (seconds: number): number =>
  Math.floor(Math.max(0, Math.floor(seconds)) / SECONDS_PER_DAY);

export const getAssessmentCountdownDayLabel = (seconds: number): string =>
  getAssessmentCountdownDays(seconds) === 1 ? "day" : "days";

export const shouldShowAssessmentCountdownDays = (seconds: number): boolean =>
  Math.max(0, Math.floor(seconds)) >= SECONDS_PER_DAY;

const formatAssessmentTimeValue = (
  total: number,
  daysStyle: "number" | "inline",
): string => {
  if (shouldShowAssessmentCountdownDays(total)) {
    const days = getAssessmentCountdownDays(total);
    return daysStyle === "inline"
      ? `${days} ${getAssessmentCountdownDayLabel(total)}`
      : String(days);
  }

  if (total >= SECONDS_PER_HOUR) {
    const hours = Math.floor(total / SECONDS_PER_HOUR);
    const mins = Math.floor((total % SECONDS_PER_HOUR) / 60);
    const secs = total % 60;
    return `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }

  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const normalizeAssessmentSeconds = (seconds: number): number =>
  Math.max(0, Math.floor(seconds));

export const getElapsedSeconds = (
  startTimeMs: number | null | undefined,
  nowMs: number = Date.now(),
): number => {
  if (!startTimeMs) {
    return 0;
  }

  return normalizeAssessmentSeconds((nowMs - startTimeMs) / 1000);
};

export const normalizeAssessmentTotalTimeSpent = (
  totalTimeSpent: number | null | undefined,
): number => {
  if (typeof totalTimeSpent !== "number" || !Number.isFinite(totalTimeSpent)) {
    return 0;
  }

  return normalizeAssessmentSeconds(totalTimeSpent);
};

/**
 * Prefer server total_time_spent (sum of per-question time_spent) plus any
 * local session elapsed since Start/resume. Do not use time_limit - time_left
 * (contest window, not active quiz time).
 */
export const getAssessmentElapsedSeconds = (
  startTimeMs: number | null | undefined,
  totalTimeSpent: number | null | undefined,
  fallbackSeconds = 0,
): number => {
  const serverTotal = normalizeAssessmentTotalTimeSpent(totalTimeSpent);
  const sessionElapsed = getElapsedSeconds(startTimeMs);

  if (startTimeMs || serverTotal > 0) {
    return serverTotal + sessionElapsed;
  }

  return fallbackSeconds;
};

// Circle timer: number only for days (`7`), label rendered separately.
export const formatAssessmentCountdown = (seconds: number): string =>
  formatAssessmentTimeValue(normalizeAssessmentSeconds(seconds), "number");

// Inline text: includes day label (`7 days`).
export const formatAssessmentDuration = (seconds: number): string =>
  formatAssessmentTimeValue(normalizeAssessmentSeconds(seconds), "inline");

export interface AssessmentTimeSegment {
  value: string;
  label: string;
}

export const getAssessmentTimeSpentSegments = (
  seconds: number,
): AssessmentTimeSegment[] => {
  const total = normalizeAssessmentSeconds(seconds);

  if (shouldShowAssessmentCountdownDays(total)) {
    const days = getAssessmentCountdownDays(total);
    return [
      {
        value: String(days),
        label: getAssessmentCountdownDayLabel(total),
      },
    ];
  }

  if (total >= SECONDS_PER_HOUR) {
    const hours = Math.floor(total / SECONDS_PER_HOUR);
    const mins = Math.floor((total % SECONDS_PER_HOUR) / 60);
    const secs = total % 60;

    return [
      { value: hours.toString().padStart(2, "0"), label: "hr" },
      { value: mins.toString().padStart(2, "0"), label: "min" },
      { value: secs.toString().padStart(2, "0"), label: "sec" },
    ];
  }

  const mins = Math.floor(total / 60);
  const secs = total % 60;

  return [
    { value: mins.toString().padStart(2, "0"), label: "min" },
    { value: secs.toString().padStart(2, "0"), label: "sec" },
  ];
};

export const getAssessmentDurationStatDisplay = (
  seconds: number,
): { value: string; label: string } => {
  const total = normalizeAssessmentSeconds(seconds);

  if (shouldShowAssessmentCountdownDays(total)) {
    return {
      value: String(getAssessmentCountdownDays(total)),
      label: getAssessmentCountdownDayLabel(total),
    };
  }

  return {
    value: formatAssessmentTimeValue(total, "number"),
    label: "Time limit",
  };
};
